import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/supabase';
import { useAuth } from './useAuth';
import { logService } from '../lib/logService';
import type { PlanTier, FeatureKey, LimitKey, PlanFeatures, PlanLimits } from '../lib/plans/types';
import { FEATURE_GATES, DEFAULT_LIMITS } from '../lib/plans/featureMap';
import { STRIPE_PRICE_IDS } from '../lib/plans/stripePriceIds';
import type { BillingCycle } from '../components/pricing/tiersData';

// =====================================================
// STORY BP-3: usePlan() hook + feature flag client
// =====================================================
// Pattern canônico replicado de useSubscription (Context + realtime postgres_changes).
// Decisões @po Pax (gate v1.1):
// - OQ1: Context + postgres_changes realtime (zero deps)
// - OQ2: caller fornece currentCount via prop (withinLimit)
// - OQ3: reuso edge function create-checkout-session; fallback /pricing se BP-2 não live
// - OQ4: explicit interfaces em lib/plans/types.ts
// - OQ5: can() binário + withinLimit() numérico
// - OQ6: híbrido FEATURE_GATES || features[f] === true

export interface UpgradeResult {
  ok: boolean;
  /** Código de erro quando `ok === false`. Ausente em sucesso. */
  error?: string;
}

export interface UsePlanReturn {
  /** Raw tier de profiles.plan_tier (pode ser 'free' mesmo em grandfather). */
  tier: PlanTier;
  /** Tier efetivo via RPC get_effective_plan (considera is_exempt + grandfather). */
  effectiveTier: PlanTier;
  /** Features JSONB do row de plans correspondente ao effectiveTier. */
  features: PlanFeatures;
  /** Limits JSONB do row de plans. -1 = unlimited. */
  limits: PlanLimits;
  /** Predicate binário: tier efetivo libera essa feature? */
  can: (feature: FeatureKey) => boolean;
  /** Predicate numérico: currentCount está dentro do limite? -1 sempre passa. */
  withinLimit: (name: LimitKey, currentCount: number) => boolean;
  /** True se grandfather_until > NOW e !is_exempt. */
  isGrandfathered: boolean;
  /** Data de expiração do grandfather (null se inaplicável). */
  grandfatherExpiresAt: Date | null;
  /**
   * Redireciona pro Stripe Checkout via edge function.
   * Retorna `{ ok: true }` se a navegação externa (Stripe URL) foi disparada,
   * ou `{ ok: false, error }` se houve falha — caller decide fallback
   * (toast, navigate SPA, etc). **NÃO** faz full-reload interno — isso
   * causava logout spúrio quando session expirava em paralelo (P0 v1.17.4).
   * Cycle default 'monthly' mantém retrocompat com callers antigos (UpgradeModal).
   */
  upgrade: (target: 'pro' | 'business', cycle?: BillingCycle) => Promise<UpgradeResult>;
  loading: boolean;
}

const PlanContext = createContext<UsePlanReturn | undefined>(undefined);

const DEFAULT_FEATURES: PlanFeatures = {};

/** Helper: checa se o error body do create-checkout-session indica endpoint unknown/unavailable. */
function isEdgeFunctionUnavailable(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const anyErr = err as Record<string, unknown>;
  const code = anyErr.code;
  const message = typeof anyErr.message === 'string' ? anyErr.message : '';
  return (
    code === 'function-not-found' ||
    message.includes('not found') ||
    message.includes('Not Found') ||
    message.includes('404')
  );
}

export const PlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [tier, setTier] = useState<PlanTier>('free');
  const [effectiveTier, setEffectiveTier] = useState<PlanTier>('free');
  const [features, setFeatures] = useState<PlanFeatures>(DEFAULT_FEATURES);
  const [limits, setLimits] = useState<PlanLimits>(DEFAULT_LIMITS.free);
  const [grandfatherUntil, setGrandfatherUntil] = useState<string | null>(null);
  const [isExempt, setIsExempt] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPlanData = useCallback(async (signal?: AbortSignal) => {
    if (!user) {
      setTier('free');
      setEffectiveTier('free');
      setFeatures(DEFAULT_FEATURES);
      setLimits(DEFAULT_LIMITS.free);
      setGrandfatherUntil(null);
      setIsExempt(false);
      setLoading(false);
      return;
    }

    try {
      // 3 queries em paralelo (AC2 Pax OQ1 — otimização de latência)
      const [rpcRes, profileRes] = await Promise.all([
        supabase.rpc('get_effective_plan', { p_user_id: user.id }),
        supabase
          .from('profiles')
          .select('plan_tier, grandfather_until, is_exempt')
          .eq('id', user.id)
          .single(),
      ]);
      // platform-loading-infinito.1 AC2(b)(i): soft-abort após Promise.all
      // (cobre RPC + profiles select). Pattern: useAdminRevenue.tsx:42-56.
      if (signal?.aborted) return;

      // effectiveTier — defensivo contra retorno inesperado
      const rawEffective = rpcRes.data;
      const validTiers: PlanTier[] = ['free', 'pro', 'business'];
      const resolvedEffective: PlanTier = validTiers.includes(rawEffective as PlanTier)
        ? (rawEffective as PlanTier)
        : 'free';
      if (rpcRes.error) {
        logService.error('plan.rpc.get_effective_plan', rpcRes.error.message, { error: rpcRes.error });
      } else if (rawEffective && !validTiers.includes(rawEffective as PlanTier)) {
        logService.error('plan.rpc.invalid_tier', String(rawEffective), {});
      }

      // profile raw data
      const profile = profileRes.data;
      const rawTier: PlanTier = validTiers.includes(profile?.plan_tier as PlanTier)
        ? (profile?.plan_tier as PlanTier)
        : 'free';

      // Fetch plans row correspondente ao effectiveTier
      const { data: planRow, error: planErr } = await supabase
        .from('plans')
        .select('features, limits')
        .eq('id', resolvedEffective)
        .single();
      // platform-loading-infinito.1 AC2(b)(ii): soft-abort após plans select.
      if (signal?.aborted) return;

      if (planErr) {
        logService.error('plan.fetch.plans_row', planErr.message, { error: planErr });
      }

      setTier(rawTier);
      setEffectiveTier(resolvedEffective);
      setFeatures((planRow?.features as PlanFeatures) ?? DEFAULT_FEATURES);
      setLimits((planRow?.limits as PlanLimits) ?? DEFAULT_LIMITS[resolvedEffective]);
      setGrandfatherUntil(profile?.grandfather_until ?? null);
      setIsExempt(profile?.is_exempt === true);
    } catch (err) {
      if (signal?.aborted) return;
      logService.error('plan.fetch', err instanceof Error ? err.message : 'Unknown error', {
        error: err as Error,
      });
    } finally {
      // Não tocar state após abort — cleanup do useEffect já encerrou o ciclo.
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [user]);

  // Initial fetch + safety-net (platform-loading-infinito.1 AC1+AC2).
  // Replica pattern useAuth.tsx:68-76: setTimeout(10s) força loading=false
  // se as queries Supabase hangarem (token refresh stuck, network flake).
  // AbortController garante soft-abort de setters após unmount/timeout.
  useEffect(() => {
    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
      setLoading(prev => {
        if (prev) {
          logService.warn('plan.fetch.timeout', 'PlanProvider: queries não completaram em 10s', {
            user_id: user?.id ?? null,
            tier_default_applied: 'free',
            version: __APP_VERSION__,
          });
        }
        return false;
      });
    }, 10_000);

    fetchPlanData(controller.signal);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [fetchPlanData, user]);

  // Realtime — re-fetcha quando profiles.plan_tier mudar (webhook BP-2 aciona)
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('plan-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        () => {
          fetchPlanData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchPlanData]);

  // Derivados de grandfather
  const grandfatherExpiresAt = useMemo<Date | null>(() => {
    if (!grandfatherUntil) return null;
    const d = new Date(grandfatherUntil);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [grandfatherUntil]);

  const isGrandfathered = useMemo(() => {
    if (isExempt) return false; // exempt NÃO é grandfather — é flag separada
    if (!grandfatherExpiresAt) return false;
    return grandfatherExpiresAt.getTime() > Date.now();
  }, [grandfatherExpiresAt, isExempt]);

  // Predicates — usam effectiveTier (não raw tier)
  const can = useCallback(
    (feature: FeatureKey): boolean => {
      // Híbrido OQ6: FEATURE_GATES (spec) || features JSONB override
      const gateList = FEATURE_GATES[feature];
      const gatedByTier = Array.isArray(gateList) && gateList.includes(effectiveTier);
      const overrideByJsonb = features[feature] === true;
      return gatedByTier || overrideByJsonb;
    },
    [effectiveTier, features]
  );

  const withinLimit = useCallback(
    (name: LimitKey, currentCount: number): boolean => {
      const cap = limits[name];
      // Ausente no JSONB → fallback pro DEFAULT_LIMITS do effectiveTier
      const effectiveCap = typeof cap === 'number' ? cap : DEFAULT_LIMITS[effectiveTier][name];
      if (effectiveCap === -1) return true; // unlimited
      return currentCount < effectiveCap;
    },
    [limits, effectiveTier]
  );

  // Upgrade — OQ3: reuso edge function com fallback stub /pricing
  const upgrade = useCallback(
    async (target: 'pro' | 'business', cycle: BillingCycle = 'monthly'): Promise<UpgradeResult> => {
      const priceId = STRIPE_PRICE_IDS[target]?.[cycle];
      if (!priceId) {
        logService.error('plan.upgrade.missing_price_id', `No price_id for ${target}/${cycle}`, {});
        return { ok: false, error: 'price_not_configured' };
      }

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          return { ok: false, error: 'not_authenticated' };
        }

        const response = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            apikey: SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ price_id: priceId }),
        });

        if (response.status === 404) {
          return { ok: false, error: 'endpoint_unavailable' };
        }

        const body = await response.json().catch(() => ({}));
        const { url, error } = body as { url?: string; error?: unknown };

        if (error) {
          if (isEdgeFunctionUnavailable(error)) {
            return { ok: false, error: 'endpoint_unavailable' };
          }
          logService.error(
            'plan.upgrade',
            typeof error === 'string' ? error : 'Upgrade failed',
            { error_detail: error }
          );
          return { ok: false, error: typeof error === 'string' ? error : 'checkout_failed' };
        }

        if (url) {
          // Navegação externa legítima (Stripe Checkout) — único caso de window.location.href.
          window.location.href = url;
          return { ok: true };
        }

        return { ok: false, error: 'unexpected_response' };
      } catch (err) {
        logService.error('plan.upgrade.exception', err instanceof Error ? err.message : 'Upgrade failed', {
          error: err as Error,
        });
        return { ok: false, error: 'network_error' };
      }
    },
    []
  );

  const value = useMemo<UsePlanReturn>(
    () => ({
      tier,
      effectiveTier,
      features,
      limits,
      can,
      withinLimit,
      isGrandfathered,
      grandfatherExpiresAt,
      upgrade,
      loading,
    }),
    [
      tier,
      effectiveTier,
      features,
      limits,
      can,
      withinLimit,
      isGrandfathered,
      grandfatherExpiresAt,
      upgrade,
      loading,
    ]
  );

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};

export const usePlan = (): UsePlanReturn => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};
